import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddNewUser from "./_addNewUser";
import MyDataGrid from "./MyDataGrid";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import { ACCOUNT_DETAIL_FONT_SX } from "../AccountDetailPolicy/accountDetailTypography";

const allowedRoles = ["PowerUser", "Administrator"];

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.25,
  px: 1.5,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.25,
  px: 1.5,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

const OUTLINE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 14,
  borderRadius: "8px",
  px: 2.5,
  color: "#475569",
  borderColor: "#cbd5e1",
  "&:hover": {
    borderColor: "#94a3b8",
    backgroundColor: "#f8fafc",
  },
};

const DIALOG_PAPER_SX = {
  borderRadius: "12px",
  width: { xs: "calc(100% - 24px)", sm: "100%" },
  m: { xs: 1.5, sm: 2 },
  overflow: "hidden",
  ...ACCOUNT_DETAIL_FONT_SX,
};

const OrangeDialogHeader = ({ title, onClose, closeDisabled }) => (
  <Box
    sx={{
      px: 3,
      py: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      backgroundColor: "#F98125",
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "1.15rem",
        color: "#FFFFFF",
        letterSpacing: "-0.02em",
        lineHeight: 1.2,
      }}
    >
      {title}
    </Typography>
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      disabled={closeDisabled}
      style={{
        padding: 0,
        margin: 0,
        border: "none",
        background: "transparent",
        color: "#FFFFFF",
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: closeDisabled ? "not-allowed" : "pointer",
        opacity: closeDisabled ? 0.6 : 1,
        boxShadow: "none",
        flexShrink: 0,
      }}
    >
      <span aria-hidden="true">×</span>
    </button>
  </Box>
);

const UserConfigurationDetailsTab = ({
  addNewUserResponse,
  editUserResponse,
  usersConfig,
  accounts,
  organizations,
  licensing,
  roleAccess,
  onReActive,
  onDelete,
  onToggle,
  handleSubmit,
  hanldeResetNewUser,
}) => {
  const [disableAddUser, setDisableAddUser] = useState(false);
  const [manageUser, setManageUser] = useState({
    selectedUser: {},
    showAdduser: false,
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(null);
  const [accountList, setaccountList] = useState([]);
  const [showUserSuccessStatus, setShowUserSuccessStatus] = useState(null);
  const { showAdduser, selectedUser } = manageUser;
  const userRole = getUserRole();

  const setModalIsOpenToTrue = () => {
    setModalIsOpen(true);
  };
  const setModalIsOpenToFalse = () => {
    setModalIsOpen(false);
  };

  const handleCancelClick = (val) => {
    setManageUser({ selectedUser: {}, showAdduser: val });
    hanldeResetNewUser();
  };

  const addNewUserMessage = addNewUserResponse?.addNewUserMessage;
  const editUserMessage = editUserResponse?.editUserMessage;
  useEffect(() => {
    if (addNewUserMessage || editUserMessage) {
      if (selectedUser && Object.keys(selectedUser).length > 0) {
        setShowUserSuccessStatus(editUserMessage);
      } else {
        setShowUserSuccessStatus(addNewUserMessage);
      }
      setTimeout(() => setShowUserSuccessStatus(null), 10000);
    }
  }, [addNewUserMessage, editUserMessage]);

  useEffect(() => {
    if (licensing) {
      const numUsers = licensing.numUsers;
      if (usersConfig) {
        if (usersConfig.length >= numUsers) {
          setDisableAddUser(true);
        } else {
          setDisableAddUser(false);
        }
      }
    }
  }, [licensing, usersConfig]);

  const handleEditUser = (user) => {
    setManageUser({ showAdduser: true, selectedUser: user });
  };

  const handleConfirmationClick = () => {
    if (showConfirmation && showConfirmation.action === "delete") {
      onDelete(showConfirmation);
    } else {
      onReActive({ email: showConfirmation.email });
    }
    setShowConfirmation(null);
  };

  const onAddUserClick = () => {
    setManageUser({ selectedUser: {}, showAdduser: true });
  };

  const isDeleteConfirm = showConfirmation?.action === "delete";
  const visibleAccounts = (accountList || []).filter(
    (acct) => !acct.accountNumber?.includes("INIT00000001")
  );

  return (
    <React.Fragment>
      <Dialog
        open={modalIsOpen}
        onClose={setModalIsOpenToFalse}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <OrangeDialogHeader title="Cloud Accounts" onClose={setModalIsOpenToFalse} />
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <TableContainer
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={HEADER_CELL_SX}>
                    Account Name
                  </TableCell>
                  <TableCell align="center" sx={HEADER_CELL_SX}>
                    Account Number
                  </TableCell>
                  <TableCell align="center" sx={HEADER_CELL_SX}>
                    Account Type
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{ ...BODY_CELL_SX, color: "#94a3b8", py: 3 }}
                    >
                      No accounts found
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleAccounts.map((acct, index) => (
                    <TableRow
                      key={acct.accountNumber || index}
                      sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                    >
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {acct.accountName || "—"}
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {acct.accountNumber || "—"}
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {acct.accountType || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

      <div style={{ position: "absolute" }}>
        {showAdduser && (
          <AddNewUser
            accounts={accounts}
            organizations={organizations}
            usersConfig={usersConfig}
            setShowAddUsers={handleCancelClick}
            selectedUser={selectedUser}
            showAdduser={showAdduser}
            handleOnSubmit={handleSubmit}
            addNewUserResponse={addNewUserResponse}
            editUserResponse={editUserResponse}
          />
        )}
      </div>

      <Dialog
        open={Boolean(showConfirmation)}
        onClose={() => setShowConfirmation(null)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{ sx: DIALOG_PAPER_SX }}
      >
        <OrangeDialogHeader
          title={isDeleteConfirm ? "Delete User" : "Resend Activation"}
          onClose={() => setShowConfirmation(null)}
        />
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 400,
              color: "#334155",
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {isDeleteConfirm ? (
              <>
                Are you sure you want to delete{" "}
                <Box component="span" sx={{ fontWeight: 600, color: "#111827" }}>
                  {showConfirmation?.username}
                </Box>
                ?
              </>
            ) : (
              "Confirm resend activation?"
            )}
          </Typography>
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1.25,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setShowConfirmation(null)}
              sx={OUTLINE_BTN_SX}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleConfirmationClick}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "8px",
                px: 2.5,
                boxShadow: "none",
                backgroundColor: isDeleteConfirm ? "#ef4444" : "#F98125",
                "&:hover": {
                  backgroundColor: isDeleteConfirm ? "#dc2626" : "#e8741c",
                  boxShadow: "none",
                },
              }}
            >
              OK
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Row>
        <Col lg={12}>
          <MyDataGrid
            usersConfig={usersConfig}
            setaccountList={setaccountList}
            setModalIsOpenToTrue={setModalIsOpenToTrue}
            handleEditUser={handleEditUser}
            roleAccess={roleAccess}
            setShowConfirmation={setShowConfirmation}
            onToggle={onToggle}
            disableAddUser={
              disableAddUser ||
              usersConfig?.length >= licensing.numUsers ||
              !allowedRoles.includes(userRole)
            }
            onAddUserClick={onAddUserClick}
          />
        </Col>
      </Row>

      <Snackbar
        open={showUserSuccessStatus !== null}
        autoHideDuration={5000}
      >
        <Alert severity="success">{showUserSuccessStatus}</Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default React.memo(UserConfigurationDetailsTab);
